import React, { useCallback, useContext } from 'react'
import { ISO_DATE_FORMAT, SHORT_DAY_NAME_DAY_MONTH, SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import styled from 'styled-components'
import { rem } from 'polished'
import { pluralizeToString } from 'lib/pluralize'
import cn from 'clsx'
import FullArrow from 'components/Common/Arrow/fullArrow'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'components/Luxkit/Button/TextButton'
import Breakpoint from 'components/Common/Breakpoint'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'

const FooterButton = styled(TextButton)`
  width: 100%;
  max-width: ${rem(280)};

  ${mediaQueryUp.sm} {
    width: auto;
    margin: 0 ${rem(8)};
  }
`

const ApplyButton = styled(TextButton)`
  &.space-top {
    margin-top: ${rem(16)};

    ${mediaQueryUp.sm} {
      margin-top: 0;
    }
  }

  ${mediaQueryUp.sm} {
    order: 1;
  }
`

const DateDurationContainer = styled.div`
  display: flex;
  font-size: ${props => props.theme.fontSize.sm};
`

const DateArrow = styled(FullArrow)`
  margin: ${rem(4)} ${rem(8)};
`

const DateWrapper = styled.div`
  display: inline-flex;
  font-weight: ${props => props.theme.font.primary.weight.semiBold};
  margin-right: ${rem(8)};
`

const ApplyDateSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${mediaQueryUp.sm} {
    flex-direction: row;
    justify-content: flex-end;
    padding: ${rem(16)} ${rem(24)};
    box-shadow: ${props => props.theme.shadow.flat.small};
  }

  > * + * {
    margin-top: ${rem(14)};

    ${mediaQueryUp.sm} {
      margin-top: 0;
    }
  }
`

interface Props {
  onApply: (withSearch?: boolean) => void;
  onDecline: () => void;
  isLastStep?: boolean;
  flexibleDateSelected?: boolean;
  label?: 'day' | 'night'
}

function SearchDateFooter({ onApply, onDecline, isLastStep, label = 'night' }: Props) {
  const { checkinDate, checkoutDate, dateSearchOptionId } = useContext(GlobalSearchStateContext)
  const hasDates = !!(checkinDate && checkoutDate)
  const disableSearchButton = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC && !hasDates

  const handleApply = useCallback(() => {
    if (isLastStep) {
      onApply(true)
    } else {
      onApply(false)
    }
  }, [isLastStep, onApply])

  return (
    <ApplyDateSection>
      {hasDates &&
        <DateDurationContainer>
          <DateWrapper>
            <time dateTime={checkinDate.format(ISO_DATE_FORMAT)}>
              {checkinDate.format(SHORT_DAY_NAME_DAY_MONTH)}
            </time>

            <DateArrow height={12} width={24} arrowSize={12}/>

            <time dateTime={checkoutDate.format(ISO_DATE_FORMAT)}>
              {checkoutDate.format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}
            </time>
          </DateWrapper>
          <span>
            ({pluralizeToString(label, checkoutDate.diff(checkinDate, 'days'))})
          </span>
        </DateDurationContainer>
      }
      {/* On sm size devices and above, show Apply button when flexible date search enabled. Otherwise keep the current behaviour */}
      <Breakpoint min="sm">
        <ApplyButton
          kind="primary"
          disabled={disableSearchButton}
          onClick={handleApply}
          data-testid="search-apply-button"
          fit="mobile-full-width"
          className={cn({ 'space-top': !hasDates })}
        >
          Apply
        </ApplyButton>
      </Breakpoint>
      {/* On mobile devices, show Search button when flexible date search enabled. Otherwise keep the current behaviour */}
      <Breakpoint max="xs">
        <ApplyButton
          kind="primary"
          disabled={disableSearchButton}
          onClick={handleApply}
          data-testid="search-apply-button"
          fit="mobile-full-width"
          className={cn({ 'space-top': !hasDates })}
        >
          Search
        </ApplyButton>
      </Breakpoint>

      <FooterButton
        kind="tertiary"
        onClick={onDecline}
        data-testid="search-nodates-button"
      >
        Cancel
      </FooterButton>
    </ApplyDateSection>
  )
}

export default SearchDateFooter
